import { useState } from 'react';
import { Button } from '../../../../../components/Button';
import { CatalogPublicationToggle } from '../CatalogPublicationToggle';
import { EventDistributionPanel } from '../EventDistributionPanel';

interface ShareWiderPanelProps {
  /** The tournament that was just published, on RosterPilot's own calendar. */
  calendarEventId: string;
  /**
   * Whether it is already in the catalog. A tournament this wizard just published never is, so the
   * default is false; the itinerary screen passes the event's real state when reusing this.
   */
  isShareable?: boolean;
  /** Heading copy, so the panel can introduce itself differently beside a request. */
  heading: string;
  lede: string;
}

/**
 * Sharing a built itinerary beyond the one program that asked for it (system_admin).
 *
 * Both mechanisms already existed and both were reachable only from the event view: staff had to
 * finish the wizard, go find the tournament again on the schedule, open it and scroll to the admin
 * block. That is the same "building and delivering are two unconnected screens" problem
 * `ShareWithProgramStep` was written to solve, and it applies just as much to an itinerary built in
 * advance — which had no share step at all, because the wizard only offered one when it was
 * launched from a request.
 *
 * The two are deliberately shown together and are NOT alternatives:
 *
 * - **Publish to the catalog** makes it discoverable. Nothing lands on anyone's calendar until a
 *   program adds it themselves. This is the one for an itinerary built ahead of anybody asking.
 * - **Send to programs** pushes a copy straight onto the chosen calendars. Re-sending updates the
 *   copies and skips any a coach has already edited.
 *
 * Composed from the existing panels rather than reimplemented, so the targeting rules, the per-org
 * outcome labels and the "publishing is the gate the cross-org catalog read rests on" behaviour stay
 * in one place. Both are `system_admin`-only server-side; every caller gates on the role too, so a
 * coach is never shown an action that can only 403.
 */
export function ShareWiderPanel({
  calendarEventId,
  isShareable = false,
  heading,
  lede,
}: ShareWiderPanelProps) {
  // Collapsed until asked for. `EventDistributionPanel` loads every organization on the platform
  // plus this event's current recipients the moment it mounts, and the common path through this
  // screen — fulfil a request, send it to the program that asked — never needs either. Mounting it
  // eagerly would spend two admin round-trips on every finish to render a panel most runs ignore.
  const [open, setOpen] = useState(false);

  return (
    <section className="wiz-share-wider" data-testid="wizard-share-wider">
      <h2 className="wiz-share__title">{heading}</h2>
      <p className="wiz-share__lede">{lede}</p>

      {open ? (
        <>
          <CatalogPublicationToggle
            event={{ id: calendarEventId, eventType: 'Tournament', isShareable }}
          />
          <EventDistributionPanel eventId={calendarEventId} />
        </>
      ) : (
        <Button
          type="button"
          variant="secondary"
          onClick={() => setOpen(true)}
          data-testid="wizard-share-wider-open"
        >
          Share with other programs
        </Button>
      )}
    </section>
  );
}
